import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { logger } from '@/shared/lib';

export interface VideoPlayerProps {
  /** Подписанная HLS-ссылка (`.m3u8`). */
  src: string;
  poster?: string | null;
  title: string;
}

const HLS_MIME = 'application/vnd.apple.mpegurl';

/**
 * Плеер HLS по подписанной ссылке (ТЗ 17.13.6: тот же поток, что в приложении): Safari играет HLS
 * сам, остальные браузеры — через hls.js, загружаемый только при открытии плеера. Поток не
 * воспроизводится — сообщение вместо пустого окна.
 */
export function VideoPlayer({ src, poster, title }: VideoPlayerProps) {
  const { t } = useTranslation('common');
  const ref = useRef<HTMLVideoElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const video = ref.current;
    if (!video) return undefined;
    setFailed(false);
    if (video.canPlayType(HLS_MIME)) {
      video.src = src;
      return () => {
        video.removeAttribute('src');
      };
    }
    let destroyed = false;
    let destroy: (() => void) | null = null;
    import('hls.js/light')
      .then(({ default: Hls }) => {
        if (destroyed) return;
        if (!Hls.isSupported()) {
          setFailed(true);
          return;
        }
        const hls = new Hls();
        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (data.fatal) setFailed(true);
        });
        hls.loadSource(src);
        hls.attachMedia(video);
        destroy = () => hls.destroy();
      })
      .catch((error: unknown) => {
        logger.warn('video player: hls.js failed to load', { error });
        if (!destroyed) setFailed(true);
      });
    return () => {
      destroyed = true;
      destroy?.();
    };
  }, [src]);

  return (
    <div className="flex flex-col gap-6">
      {/* biome-ignore lint/a11y/useMediaCaption: субтитров у видео урока в контракте нет */}
      <video
        ref={ref}
        controls
        playsInline
        poster={poster ?? undefined}
        aria-label={title}
        className="aspect-video w-full max-w-640 rounded-12 bg-ink-900"
      />
      {failed ? (
        <p role="alert" className="m-0 text-[12px] text-status-error">
          {t('video.playbackFailed')}
        </p>
      ) : null}
    </div>
  );
}
